/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    ContentVodItemKernel,
    FolderVodItemKernel,
    OfferVodItemKernel,
    ScreenType,
    UnifiedCollectionVodItemKernel,
    VodItem,
    VodItemList,
    VodItemType,
} from 'client-code/vod-browse';

import { ContentType, InteractiveComponents } from '../../../core/enums';
import { TransitionScreenType } from '../../../core/enums/TransitionScreenType';
import { IInfoCard, InfoCardManager } from '../../../core/infocard';
import { Log } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import type { AppDispatch, TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import { TListViewType, TVodPlayActionStateType } from '../../../core/types';
import { VodBrowseInfoCardUserInputHandler } from '../../../core/usecases/VodBrowseInfoCardUserInputHandler';
import { CcbSwitch } from '../../../core/utils/ccb';
import { CcbSwitchName } from '../../../core/utils/ccb/enums';
import {
    biaxialWidgetClearDataAction,
    biaxialWidgetUpdateHorizontalItemsAction,
    biaxialWidgetUpdateHorizontalPositionAction,
} from '../../biaxialwidget/biaxialwidget.actions';
import { BWHListType, SCROLL_DIRECTION } from '../../biaxialwidget/biaxialwidget.props';
import {
    biaxialWidgetDisableTurboScroll,
    biaxialWidgetEnableTurboScroll,
} from '../../biaxialwidget/biaxialwidget.reducers';
import {
    onKeyDownBiaxialWidget,
    onKeyLeftBiaxialWidget,
    onKeyRightBiaxialWidget,
    onKeyUpBiaxialWidget,
    onMouseOutBiaxialWidget,
    onMouseOverBiaxialWidget,
} from '../../biaxialwidget/navigation/biaxialwidgetnavigation.reducers';
import {
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
} from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    onKeyOkVodPlayErrorOverlay,
    onMouseClickVodPlayErrorOverlay,
} from '../../overlay/vodplayerroroverlay/vodplayerroroverlay.reducers';
import {
    onKeyOkVodUpSellOverlay,
    onMouseClickVodUpSellOverlay,
} from '../../overlay/vodupselloverlay/vodupselloverlay.reducers';
import {
    hideOverlayDialog,
    onKeyDownOverlayDialog,
    onKeyUpOverlayDialog,
    onMouseOutOverlayDialogOperations,
    onMouseOverOverlayDialogOperations,
} from '../../overlaydialog/overlaydialog.reducers';
import {
    collectionScreenTransition,
    galleryScreenTransition,
    programScreenTransition,
} from '../../screens/navigation/screensnavigation';
import {
    showGuideScreen,
    showListViewScreen,
    showPreviousScreen,
} from '../../screens/screens.reducers';
import { notifyVodPlayActionState } from '../../vodplayaction/vodplayaction.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';
import {
    vodBrowseMainScreenClearDataAction,
    vodBrowseMainScreenUpdateHorizontalPositionsAction,
} from '../vodbrowsemainscreen.actions';
import { vodBrowseMainScreenUpdateHorizontalItems } from '../vodbrowsemainscreen.reducers';
import { NAVIGATION_DIRECTION, TNavigationDirection } from './vodbrowsemainscreennavigation.props';

const TAG: string = 'vodbrowsemainscreennavigation.reducers';

// Helper function for handling mouse click navigation on default screen
const handleDefaultScreenMouseClick = (value: string) => (dispatch: Function) => {
    switch (value) {
        case InteractiveComponents.EAST:
            dispatch(onKeyRightVodBrowseMainScreen());
            break;
        case InteractiveComponents.NORTH:
            dispatch(onKeyUpVodBrowseMainScreen());
            break;
        case InteractiveComponents.OK:
            dispatch(onKeyOkVodBrowseMainScreen());
            break;
        case InteractiveComponents.SOUTH:
            dispatch(onKeyDownVodBrowseMainScreen());
            break;
        case InteractiveComponents.WEST:
            dispatch(onKeyLeftVodBrowseMainScreen());
            break;
        default:
    }
};

// Helper function for handling vertical navigation
const handleVerticalNavigation =
    (direction: TNavigationDirection) => (dispatch: Function, getState: TGetState) => {
        const { verticalPosition, horizontalPosition } = getState().biaxialWidget;
        const { horizontalPositions, verticalItems } = getState().vodBrowseMainScreen;

        const isUpDirection = direction === NAVIGATION_DIRECTION.UP;
        const positionIncrement = isUpDirection ? -1 : 1;
        const newVerticalPosition = verticalPosition + positionIncrement;

        // Check bounds with descriptive variables
        const minValidPosition = 0;
        const maxValidPosition = verticalItems.length - 1;
        const isOutOfBounds = isUpDirection
            ? newVerticalPosition < minValidPosition
            : newVerticalPosition > maxValidPosition;

        if (isOutOfBounds) {
            Log.i(TAG, `No more items to navigate ${direction}.`);
            return;
        }

        dispatch(
            biaxialWidgetUpdateHorizontalItemsAction({
                listType: BWHListType.VOD_BROWSE_MAIN,
                listItems: [],
                isEmpty: false,
            }),
        );

        // Update positions and dispatch actions
        horizontalPositions[verticalPosition] = horizontalPosition;
        dispatch(
            biaxialWidgetUpdateHorizontalPositionAction(horizontalPositions[newVerticalPosition]),
        );
        dispatch(vodBrowseMainScreenUpdateHorizontalPositionsAction(horizontalPositions));

        const parentVodItemId: string =
            verticalItems[newVerticalPosition].vodItemKernel.vodItemId ?? '';
        dispatch(vodBrowseMainScreenUpdateHorizontalItems(parentVodItemId, newVerticalPosition));

        const biaxialNavigationAction = isUpDirection
            ? onKeyUpBiaxialWidget()
            : onKeyDownBiaxialWidget();
        dispatch(biaxialNavigationAction);
    };

// Key Navigations
const handleOnKeyOkVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { verticalPosition, horizontalPosition } = getState().biaxialWidget;
    const { horizontalItems } = getState().vodBrowseMainScreen;
    const selectedHStrip: VodItemList = horizontalItems?.[verticalPosition];
    const selectedItem: VodItem | undefined = selectedHStrip?.vodItemList?.[horizontalPosition];
    if (!selectedItem) {
        Log.i(TAG, 'No item selected on OK key press.');
        return;
    }

    const { vodItemKernel, vodItemType, title } = selectedItem;

    switch (vodItemType.toLowerCase()) {
        case VodItemType.FolderVodItem.toLowerCase(): {
            const { isAdult, vodItemId, partnerId, screenType } =
                vodItemKernel as FolderVodItemKernel;
            if (screenType === ScreenType.TextCurrentRentals) {
                dispatch(
                    showListViewScreen({
                        id: vodItemId ?? '',
                        title: title ?? '',
                        listType: screenType as TListViewType,
                    }),
                );
                Log.i(TAG, `Navigating to list view screen with id: ${vodItemId}`);
            } else {
                dispatch(
                    galleryScreenTransition(
                        !!isAdult,
                        vodItemId ?? '',
                        partnerId,
                        screenType,
                        title ?? '',
                    ),
                );

                Log.i(TAG, `Navigating to gallery screen with parentVodItemId: ${vodItemId}`);
            }
            break;
        }

        case VodItemType.EpisodeVodItem.toLowerCase():
        case VodItemType.MovieVodItem.toLowerCase():
        case VodItemType.SpecialVodItem.toLowerCase(): {
            const { partnerId, contentSupplierPartnerId, offerId } =
                vodItemKernel as OfferVodItemKernel;

            dispatch(
                programScreenTransition(
                    {
                        id: offerId ?? '',
                        contentType: ContentType.VOD_OFFER_ID,
                        partnerId: partnerId ?? '',
                        contentSupplierPartnerId: contentSupplierPartnerId ?? '',
                    },
                    showWhisper,
                ),
            );
            Log.i(TAG, `Navigating to program screen with offerId: ${offerId}`);
            break;
        }
        case VodItemType.UnifiedCollectionVodItem.toLowerCase(): {
            const { partnerId, unifiedCollectionId, collapsedVodItemId, contentSupplierPartnerId } =
                vodItemKernel as UnifiedCollectionVodItemKernel;
            dispatch(
                collectionScreenTransition(
                    {
                        unifiedCollectionId,
                        transitionScreen: '' as TransitionScreenType,
                        partnerId,
                        collapsedVodItemId,
                        contentSupplierPartnerId,
                    },
                    showWhisper,
                ),
            );
            Log.i(
                TAG,
                `Navigating to collection screen with unifiedCollectionId: ${unifiedCollectionId}`,
            );
            break;
        }
        default:
    }
};

export const onKeyBackVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
        case ScreenTaskControllerTypes.VOD_BROWSE_ERROR_OVERLAY_TASKS:
            dispatch(vodBrowseMainScreenClearDataAction());
            dispatch(biaxialWidgetClearDataAction());
            dispatch(showPreviousScreen());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyBackPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(hideOverlayDialog());
            break;
        default:
    }
};

export const onKeyDigitVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyDigitPcPinOverlay());
    }
};

export const onKeyDownVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(handleVerticalNavigation(NAVIGATION_DIRECTION.DOWN));
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDownPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(onKeyDownOverlayDialog());
            break;
        default:
    }
};

export const onKeyGuideVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(showGuideScreen());
    Log.i(TAG, 'Navigating to Guide Screen from Vod Browse Main Screen.');
};

export const onKeyInfoVodBrowseMainScreen = () => (dispatch: AppDispatch, getState: TGetState) => {
    // TODO: Remove Info Card CCB switch once feature is completed
    if (
        !CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_INFO_CARD_REMOVE_IN_KST_12193,
        )
    )
        return;

    const { horizontalItems } = getState().vodBrowseMainScreen;
    const { verticalPosition, horizontalPosition } = getState().biaxialWidget;
    const selectedHStrip: VodItemList = horizontalItems?.[verticalPosition];
    const selectedItem: VodItem | undefined = selectedHStrip?.vodItemList?.[horizontalPosition];
    if (!selectedItem) {
        Log.i(TAG, 'No item selected on Info key press.');
        return;
    }
    if (selectedItem.vodItemType.toLowerCase() === VodItemType.FolderVodItem.toLowerCase()) {
        Log.i(TAG, 'Info Card is not shown for Folder Vod Items.');
        return;
    }

    const nonFolderItems =
        selectedHStrip.vodItemList?.filter(
            (item) => item.vodItemType.toLowerCase() !== VodItemType.FolderVodItem.toLowerCase(),
        ) || [];
    const infoCard: IInfoCard = InfoCardManager.getInstance();
    const vodBrowseInfoCardUserInputHandler = new VodBrowseInfoCardUserInputHandler();
    infoCard.addUserInputEventHandler(vodBrowseInfoCardUserInputHandler);
    infoCard.setProperties({
        hasItemsBefore: nonFolderItems.length > 1,
        hasItemsAfter: nonFolderItems.length > 1,
    });
    infoCard.setContextData(VodBrowseInfoCardUserInputHandler.name, {
        activeItemIndex: horizontalPosition,
        itemList: selectedHStrip.vodItemList,
    });
    infoCard.setInfoCardKernel(vodBrowseInfoCardUserInputHandler.getInfoCardKernel(selectedItem));
    infoCard.show();
};

export const onKeyLeftVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(onKeyLeftBiaxialWidget());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyLeftPcPinOverlay());
            break;
        default:
    }
};
export const onKeyLongDownStartVodBrowseMainScreen =
    () => (dispatch: Function, getState: TGetState) => {
        const { verticalPosition, horizontalPosition } = getState().biaxialWidget;
        const { horizontalPositions, verticalItems } = getState().vodBrowseMainScreen;

        const isOutOfBounds = verticalPosition + 1 >= verticalItems.length;
        if (isOutOfBounds) {
            Log.i(TAG, `No more items to navigate ${SCROLL_DIRECTION.DOWN}.`);
            return;
        }

        horizontalPositions[verticalPosition] = horizontalPosition;
        dispatch(vodBrowseMainScreenUpdateHorizontalPositionsAction(horizontalPositions));
        dispatch(biaxialWidgetEnableTurboScroll(SCROLL_DIRECTION.DOWN));
    };

export const onKeyLongDownStopVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(stopVerticalTurboScroll());
};

export const onKeyLongLeftStartVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(biaxialWidgetEnableTurboScroll(SCROLL_DIRECTION.LEFT));
};

export const onKeyLongLeftStopVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(biaxialWidgetDisableTurboScroll());
};

export const onKeyLongOkVodBrowseMainScreen = () => () => {
    // TODO: Implement the logic for handling the long ok key in the Vod Browse Main Screen.
};

export const onKeyLongRightStartVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(biaxialWidgetEnableTurboScroll(SCROLL_DIRECTION.RIGHT));
};

export const onKeyLongRightStopVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(biaxialWidgetDisableTurboScroll());
};

export const onKeyLongUpStartVodBrowseMainScreen =
    () => (dispatch: Function, getState: TGetState) => {
        const { verticalPosition, horizontalPosition } = getState().biaxialWidget;
        const { horizontalPositions } = getState().vodBrowseMainScreen;

        const isOutOfBounds = verticalPosition - 1 < 0;
        if (isOutOfBounds) {
            Log.i(TAG, `No more items to navigate ${SCROLL_DIRECTION.UP}.`);
            return;
        }

        horizontalPositions[verticalPosition] = horizontalPosition;
        dispatch(vodBrowseMainScreenUpdateHorizontalPositionsAction(horizontalPositions));
        dispatch(biaxialWidgetEnableTurboScroll(SCROLL_DIRECTION.UP));
    };

export const onKeyLongUpStopVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(stopVerticalTurboScroll());
};

export const onKeyOkVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(handleOnKeyOkVodBrowseMainScreen());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyOkPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_BROWSE_ERROR_OVERLAY_TASKS:
            dispatch(showPreviousScreen());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onKeyOkVodPlayErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onKeyOkVodUpSellOverlay());
            break;
        default:
    }
};

export const onKeyPlayVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { verticalPosition, horizontalPosition } = getState().biaxialWidget;
    const { horizontalItems, verticalItems } = getState().vodBrowseMainScreen;

    if (!horizontalItems || !verticalItems) {
        Log.i(TAG, 'No horizontal or vertical items available for play action.');
        return;
    }

    const selectedHStrip: VodItemList = horizontalItems?.[verticalPosition];
    const selectedItem: VodItem | undefined = selectedHStrip?.vodItemList?.[horizontalPosition];

    if (!selectedItem) {
        Log.i(TAG, 'No item selected for play action.');
        return;
    }

    const { vodItemType, vodItemKernel } = selectedItem;

    // Check if the selected item is a playable VOD item type
    const playableVodItemTypes = [
        VodItemType.EpisodeVodItem.toLowerCase(),
        VodItemType.MovieVodItem.toLowerCase(),
        VodItemType.SpecialVodItem.toLowerCase(),
        VodItemType.UnifiedCollectionVodItem.toLowerCase(),
    ];

    if (!playableVodItemTypes.includes(vodItemType.toLowerCase())) {
        Log.i(TAG, `Selected item type ${vodItemType} is not playable.`);
        return; // Exit early if not a playable VOD item type
    }

    // Handle different playable item types
    if (vodItemType.toLowerCase() === VodItemType.EpisodeVodItem.toLowerCase()) {
        const { contentId, partnerId, collapsedVodItemId } = vodItemKernel as ContentVodItemKernel;
        dispatch(
            notifyVodPlayActionState({
                type: TVodPlayActionStateType.INIT,
                vodRequiredParams: {
                    id: contentId ?? '',
                    itemType: 'content',
                    partnerId: partnerId ?? '',
                    collapsedVodItemId: collapsedVodItemId ?? '',
                },
            }),
        );
    }
    // For UnifiedCollectionVodItem
    else if (vodItemType.toLowerCase() === VodItemType.UnifiedCollectionVodItem.toLowerCase()) {
        const { unifiedCollectionId, partnerId, collapsedVodItemId } =
            vodItemKernel as UnifiedCollectionVodItemKernel;
        dispatch(
            notifyVodPlayActionState({
                type: TVodPlayActionStateType.INIT,
                vodRequiredParams: {
                    id: unifiedCollectionId ?? '',
                    itemType: 'unifiedCollection',
                    partnerId: partnerId ?? '',
                    collapsedVodItemId: collapsedVodItemId ?? '',
                },
            }),
        );
    } else {
        // For MovieVodItem and SpecialVodItem
        const { offerId, partnerId } = vodItemKernel as OfferVodItemKernel;
        dispatch(
            notifyVodPlayActionState({
                type: TVodPlayActionStateType.INIT,
                vodRequiredParams: {
                    id: offerId ?? '',
                    itemType: 'offer',
                    partnerId: partnerId ?? '',
                },
            }),
        );
    }
};

export const onKeyRightVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(onKeyRightBiaxialWidget());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyRightPcPinOverlay());
            break;
        default:
    }
};

export const onKeyUpVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(handleVerticalNavigation(NAVIGATION_DIRECTION.UP));
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyUpPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(onKeyUpOverlayDialog());
            break;
        default:
    }
};

// Mouse Navigations
export const onMouseClickVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;
    const { inputFactory } = getState();
    const { value } = inputFactory;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(handleDefaultScreenMouseClick(value));
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseClickPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onMouseClickVodPlayErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseClickVodUpSellOverlay());
            break;
        default:
    }
};

export const onMouseOkLongPressStartVodBrowseMainScreen =
    () => (dispatch: Function, getState: TGetState) => {
        const { inputFactory } = getState();
        const { value } = inputFactory;
        switch (value) {
            case InteractiveComponents.EAST:
                dispatch(onKeyLongRightStartVodBrowseMainScreen());
                break;
            case InteractiveComponents.NORTH:
                dispatch(onKeyLongUpStartVodBrowseMainScreen());
                break;
            case InteractiveComponents.OK:
                dispatch(onKeyLongOkVodBrowseMainScreen());
                break;
            case InteractiveComponents.SOUTH:
                dispatch(onKeyLongDownStartVodBrowseMainScreen());
                break;
            case InteractiveComponents.WEST:
                dispatch(onKeyLongLeftStartVodBrowseMainScreen());
                break;
            default:
        }
    };

export const onMouseOkLongPressStopVodBrowseMainScreen =
    () => (dispatch: Function, getState: TGetState) => {
        const { inputFactory } = getState();
        const { value } = inputFactory;
        switch (value) {
            case InteractiveComponents.EAST:
                dispatch(onKeyLongRightStopVodBrowseMainScreen());
                break;
            case InteractiveComponents.NORTH:
                dispatch(onKeyLongUpStopVodBrowseMainScreen());
                break;
            case InteractiveComponents.SOUTH:
                dispatch(onKeyLongDownStopVodBrowseMainScreen());
                break;
            case InteractiveComponents.WEST:
                dispatch(onKeyLongLeftStopVodBrowseMainScreen());
                break;
            default:
        }
    };

export const onMouseOutVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(onMouseOutBiaxialWidget());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOutPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseOutOverlayDialogOperations());
            break;
        default:
    }
};

export const onMouseOverVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(onMouseOverBiaxialWidget());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOverPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseOverOverlayDialogOperations());
            break;
        default:
    }
};

const stopVerticalTurboScroll = () => (dispatch: Function, getState: TGetState) => {
    const { verticalPosition } = getState().biaxialWidget;
    const { verticalItems } = getState().vodBrowseMainScreen;

    dispatch(biaxialWidgetDisableTurboScroll());
    const parentVodItemId: string = verticalItems[verticalPosition].vodItemKernel.vodItemId ?? '';
    dispatch(vodBrowseMainScreenUpdateHorizontalItems(parentVodItemId, verticalPosition));
};
